import React, { useState, useEffect } from "react";
import {
  Stack,
  Dropdown,
  IDropdownOption,
  DatePicker,
  Text,
  DefaultButton,
  IconButton,
  Panel,
  PanelType
} from "@fluentui/react";

export interface DateRange {
  start?: Date;
  end?: Date;
}

interface Props {
  onDateRangeChange: (range: DateRange) => void;
  disabled?: boolean;
  resetTrigger?: number; // External trigger to reset the component
}

export enum DateRangePreset {
  PastHour = "past_hour",
  Past4Hours = "past_4_hours", 
  Past8Hours = "past_8_hours",
  CustomRange = "custom_range"
}

const DateRangePicker = ({ onDateRangeChange, disabled, resetTrigger }: Props) => {
  const [selectedPreset, setSelectedPreset] = useState<string>("all");
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>();
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>();
  const [isCustomPanelOpen, setIsCustomPanelOpen] = useState(false);

  const presetOptions: IDropdownOption[] = [
    { key: "all", text: "All Time" },
    { key: DateRangePreset.PastHour, text: "Past Hour" },
    { key: DateRangePreset.Past4Hours, text: "Past 4 Hours" },
    { key: DateRangePreset.Past8Hours, text: "Past 8 Hours" },
    { key: DateRangePreset.CustomRange, text: "Custom Range..." }
  ];

  const getPresetDateRange = (preset: string): DateRange => {
    const now = new Date();
    
    switch (preset) {
      case DateRangePreset.PastHour:
        return {
          start: new Date(now.getTime() - 60 * 60 * 1000), // 1 hour ago
          end: now
        };
      case DateRangePreset.Past4Hours:
        return {
          start: new Date(now.getTime() - 4 * 60 * 60 * 1000), // 4 hours ago
          end: now
        };
      case DateRangePreset.Past8Hours:
        return {
          start: new Date(now.getTime() - 8 * 60 * 60 * 1000), // 8 hours ago
          end: now
        };
      case DateRangePreset.CustomRange:
        return {
          start: customStartDate,
          end: customEndDate
        };
      default: // "all"
        return {};
    }
  };

  useEffect(() => {
    if (selectedPreset !== DateRangePreset.CustomRange) {
      const range = getPresetDateRange(selectedPreset);
      onDateRangeChange(range);
    }
  }, [selectedPreset]);

  useEffect(() => {
    if (selectedPreset === DateRangePreset.CustomRange) {
      const range = getPresetDateRange(selectedPreset);
      onDateRangeChange(range);
    }
  }, [customStartDate, customEndDate, selectedPreset]);

  // Reset component when external reset trigger changes
  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger > 0) {
      setSelectedPreset("all");
      setCustomStartDate(undefined);
      setCustomEndDate(undefined);
      setIsCustomPanelOpen(false);
    }
  }, [resetTrigger]);

  const handlePresetChange = (_, option?: IDropdownOption) => {
    if (option) {
      const preset = option.key as string;
      setSelectedPreset(preset);
      
      if (preset === DateRangePreset.CustomRange) {
        setIsCustomPanelOpen(true);
      }
    }
  };

  const handleCustomRangeApply = () => {
    setIsCustomPanelOpen(false);
    const range = getPresetDateRange(DateRangePreset.CustomRange);
    onDateRangeChange(range);
  };

  const handleCustomRangeCancel = () => {
    setIsCustomPanelOpen(false);
    // Reset to "All Time" if user cancels custom range
    setSelectedPreset("all");
    setCustomStartDate(undefined);
    setCustomEndDate(undefined);
  };

  const clearCustomDates = () => {
    setCustomStartDate(undefined);
    setCustomEndDate(undefined);
  };

  // Format display text for custom range
  const getCustomRangeDisplayText = () => {
    if (selectedPreset === DateRangePreset.CustomRange) {
      if (customStartDate && customEndDate) {
        return `${customStartDate.toLocaleDateString()} - ${customEndDate.toLocaleDateString()}`;
      } else if (customStartDate) {
        return `From ${customStartDate.toLocaleDateString()}`;
      } else if (customEndDate) {
        return `Until ${customEndDate.toLocaleDateString()}`;
      }
      return "Custom Range...";
    }
    return presetOptions.find(opt => opt.key === selectedPreset)?.text || "All Time";
  };

  return (
    <>
      <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
        <Dropdown
          placeholder="Filter by date"
          options={presetOptions}
          selectedKey={selectedPreset}
          onChange={handlePresetChange}
          styles={{
            dropdown: { minWidth: 150 },
            title: { border: "1px solid #ccc", height: 32 }
          }}
          disabled={disabled}
        />
      </Stack>

      <Panel
        headerText="Custom Date Range"
        isOpen={isCustomPanelOpen}
        onDismiss={handleCustomRangeCancel}
        type={PanelType.smallFixedFar}
        closeButtonAriaLabel="Close"
      >
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: "20px 0" } }}>
          <Stack tokens={{ childrenGap: 8 }}>
            <Text variant="medium" styles={{ root: { fontWeight: "600" } }}>
              Start Date (Optional)
            </Text>
            <DatePicker
              placeholder="Select start date"
              value={customStartDate}
              onSelectDate={setCustomStartDate}
              maxDate={customEndDate || new Date()}
              styles={{
                textField: { width: "100%" }
              }}
            />
          </Stack>

          <Stack tokens={{ childrenGap: 8 }}>
            <Text variant="medium" styles={{ root: { fontWeight: "600" } }}>
              End Date (Optional)
            </Text>
            <DatePicker
              placeholder="Select end date"
              value={customEndDate}
              onSelectDate={setCustomEndDate}
              minDate={customStartDate}
              maxDate={new Date()}
              styles={{
                textField: { width: "100%" }
              }}
            />
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end" styles={{ root: { marginTop: "20px" } }}>
            <DefaultButton
              text="Cancel"
              onClick={handleCustomRangeCancel}
            />
            <DefaultButton
              text="Apply"
              primary
              onClick={handleCustomRangeApply}
            />
          </Stack>
        </Stack>
      </Panel>
    </>
  );
};

export default DateRangePicker;
